import { Category } from '@buf/upcyclers_gateway.bufbuild_es/product/v2/product_pb';
import { create } from 'zustand';

/**
 * `HeaderState` is an interface defining the state and actions for managing the header.
 * This state handles the visibility of the menu sheet in the header.
 *
 * - `openSheet`: Whether the sheet is open or closed.
 * - `setOpenSheet`: Function to update the `openSheet` state, controlling whether the sheet is visible or not.
 * - `searchExpanded`: Whether the search interface is expanded or collapsed.
 * - `setSearchExpanded`: Function to update the `searchExpanded` state, controlling whether the search interface is visible or not.
 */
interface HeaderState {
  openSheet: boolean;
  setOpenSheet: (openSheet: boolean) => void;
  searchExpanded: boolean;
  setSearchExpanded: (searchExpanded: boolean) => void;
  category: Category;
  setCategory: (category: Category) => void;
}

/**
 * `useHeaderStore` is a Zustand store for managing the state of the header.
 * Specifically, it tracks whether the menu sheet is open or closed.
 *
 * This store allows for:
 * - Managing the state of the `openSheet` visibility.
 * - Providing a function `setOpenSheet` to toggle the state of `openSheet`.
 * - Managing the state of `searchExpanded`, which controls the expansion of the search bar.
 * - Providing a function `setSearchExpanded` to update the `searchExpanded` state.
 */
const useHeaderStore = create<HeaderState>((set) => ({
  openSheet: false,
  setOpenSheet: (openSheet: boolean) => set({ openSheet }),
  searchExpanded: false,
  setSearchExpanded: (searchExpanded: boolean) => set({ searchExpanded }),
  category: Category.UNSPECIFIED,
  setCategory: (category: Category) => set({ category }),
}));

export { useHeaderStore };
